import React, { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { differenceInDays, differenceInMonths, differenceInWeeks, isToday, isTomorrow } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import styled, { useTheme } from 'styled-components/native'

import { numberToOrdinalWord } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/stringUtils'
import { Programs } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programConfigs'
import { EpisodeStates } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'

import { ClientDetailsSideBarSection, ClientDetailsSideBarSectionItem } from './ClientDetailsSideBarSection'
import { BodyText } from '../../atoms/bodyText/BodyText'
import { Display } from '../../atoms/display/Display'
import { Divider } from '../../atoms/divider/Divider'
import { BodyTextSize, DisplaySize } from '../../styles/typeStyles'
import { Details } from '../../ui-models/messages/Details'

const SectionWrapper = styled.View({
  flexDirection: 'column',
})

const StyledDivider = styled(Divider)(({ theme }) => ({
  paddingLeft: theme.spacing['24px'],
  paddingRight: theme.spacing['24px'],
}))

const ContentWrapper = styled.View(({ theme }) => ({
  paddingTop: theme.spacing['32px'],
  paddingLeft: theme.spacing['24px'],
  paddingRight: theme.spacing['24px'],
  flexDirection: 'column',
  gap: theme.spacing['8px'],
}))

export type ClientDetailsTherapySideBarSectionProps = {
  details: Details
}

const formatMeetingType = (meetingFormat: MEETING_FORMATS, intl: ReturnType<typeof useIntl>): string => {
  switch (meetingFormat) {
    case MEETING_FORMATS.VIDEO:
      return intl.formatMessage({ defaultMessage: 'Video', description: 'Video session type' })
    case MEETING_FORMATS.PHONE:
    case MEETING_FORMATS.CALL:
      return intl.formatMessage({ defaultMessage: 'Phone', description: 'Phone session type' })
    case MEETING_FORMATS.IN_PERSON:
    case MEETING_FORMATS.IN_PERSON_UNDERSCORE:
      return intl.formatMessage({ defaultMessage: 'In-person', description: 'In-person session type' })
    case MEETING_FORMATS.LIVE_MESSAGING:
      return intl.formatMessage({ defaultMessage: 'Live messaging', description: 'Live messaging session type' })
    default:
      return intl.formatMessage({ defaultMessage: 'Video', description: 'Video session type' })
  }
}

export const ClientDetailsTherapySideBarSection: React.FC<ClientDetailsTherapySideBarSectionProps> = ({ details }) => {
  const intl = useIntl()
  const { colors } = useTheme()

  const { summaryText, items, careTypeCapitalized } = useMemo(() => {
    const { client, episode, appointment } = details

    const sessionItems: ClientDetailsSideBarSectionItem[] = []

    const clientFirstName =
      client.firstName ||
      client.preferredName ||
      intl.formatMessage({ defaultMessage: 'Client', description: 'Default client name when name is unavailable' })

    // Get care type label from ProgramConfig
    const getCareTypeLabel = (programName: string | null | undefined): string => {
      const sessionSummary = Programs.getProgramConfigByName(programName ?? undefined)?.displayLabels.sessionSummary
      return (
        sessionSummary ||
        intl.formatMessage({ defaultMessage: 'therapy', description: 'Default care type: therapy fallback' })
      )
    }

    // Get time variation (today, tomorrow, in # days, etc.)
    const getTimeVariation = (date: Date): string => {
      if (isToday(date)) {
        return intl.formatMessage({ defaultMessage: 'today', description: 'Time variation: today' })
      }

      if (isTomorrow(date)) {
        return intl.formatMessage({ defaultMessage: 'tomorrow', description: 'Time variation: tomorrow' })
      }

      const now = new Date()
      const diffDays = differenceInDays(date, now)
      const diffWeeks = differenceInWeeks(date, now)
      const diffMonths = differenceInMonths(date, now)

      if (diffDays < 7) {
        return intl.formatMessage(
          { defaultMessage: 'in {days} days', description: 'Time variation: in X days' },
          { days: diffDays },
        )
      }

      if (diffWeeks === 1) {
        return intl.formatMessage({ defaultMessage: 'next week', description: 'Time variation: next week' })
      }

      if (diffWeeks < 4) {
        return intl.formatMessage(
          { defaultMessage: 'in {weeks} weeks', description: 'Time variation: in X weeks' },
          { weeks: diffWeeks },
        )
      }

      if (diffMonths === 1) {
        return intl.formatMessage({ defaultMessage: 'next month', description: 'Time variation: next month' })
      }

      return intl.formatMessage(
        { defaultMessage: 'in {months} months', description: 'Time variation: in X months' },
        { months: diffMonths },
      )
    }

    const careType = getCareTypeLabel(episode.programName)
    // Capitalize first letter for section header
    const careTypeCapitalized = careType.charAt(0).toUpperCase() + careType.slice(1)

    // Summary line: "[Client name]'s [X] session of [care type] is [time variation]"
    let summaryText = ''
    if (appointment && episode.nextSession) {
      const nextSessionDate = new Date(episode.nextSession)
      const sessionNumber = episode.sessionNumber ?? null
      const timeVariation = getTimeVariation(nextSessionDate)

      if (sessionNumber) {
        const ordinalNumber = numberToOrdinalWord(sessionNumber)
        summaryText = intl.formatMessage(
          {
            defaultMessage: "{clientName}'s {ordinal} session of {careType} is {timeVariation}",
            description: 'Summary of next therapy session with ordinal number',
          },
          { clientName: clientFirstName, ordinal: ordinalNumber, careType, timeVariation },
        )
      } else {
        summaryText = intl.formatMessage(
          {
            defaultMessage: "{clientName}'s next session of {careType} is {timeVariation}",
            description: 'Summary of next therapy session without ordinal number',
          },
          { clientName: clientFirstName, careType, timeVariation },
        )
      }
    } else {
      // No appointment scheduled
      summaryText = intl.formatMessage(
        {
          defaultMessage: "{clientName}'s next session is unscheduled",
          description: 'Summary when no session is scheduled',
        },
        { clientName: clientFirstName },
      )
    }

    // Next session - format as "Jan 7, 2025 at 3:00 PM (PST)" (locale-aware)
    if (appointment && episode.nextSession) {
      const nextSessionDate = new Date(episode.nextSession)
      const zonedDate = utcToZonedTime(nextSessionDate, appointment.timeZone)

      // Use locale-aware formatting
      const dateString = intl.formatDate(zonedDate, { month: 'short', day: 'numeric', year: 'numeric' })
      const timeString = intl.formatTime(zonedDate, { hour: 'numeric', minute: '2-digit' })

      // Get timezone abbreviation (e.g., PST, EST)
      const timezonePart = intl
        .formatDateToParts(zonedDate, { timeZoneName: 'short' })
        .find((part) => part.type === 'timeZoneName')
      const timezoneAbbr = timezonePart?.value || appointment.timeZone

      const nextSessionDateTime = intl.formatMessage(
        {
          defaultMessage: '{date} at {time} ({timezone})',
          description: 'Formatted next session date, time, and timezone',
        },
        { date: dateString, time: timeString, timezone: timezoneAbbr },
      )

      sessionItems.push({
        key: 'next-session',
        label: <FormattedMessage defaultMessage='Next Session' description='Label for next session' />,
        textValue: nextSessionDateTime,
        testID: 'ClientDetails-next-session',
      })
    } else {
      sessionItems.push({
        key: 'next-session',
        label: <FormattedMessage defaultMessage='Next Session' description='Label for next session' />,
        textValue: intl.formatMessage({ defaultMessage: 'Unscheduled', description: 'Next session is unscheduled' }),
        testID: 'ClientDetails-next-session',
      })
    }

    // Status - map episode state to Active, Ended, or Graduated
    let statusLabel = intl.formatMessage({ defaultMessage: 'Unknown', description: 'Episode status: unknown' })
    const episodeState = episode.state

    if (episodeState === EpisodeStates.IN_PROGRESS) {
      statusLabel = intl.formatMessage({ defaultMessage: 'Active', description: 'Episode status: active' })
    } else if (episodeState === EpisodeStates.COMPLETED) {
      statusLabel = intl.formatMessage({ defaultMessage: 'Graduated', description: 'Episode status: graduated' })
    } else if (episodeState) {
      // All other states (drop_out, not_supported, care_transition, not_initiated, not_eligible, booster) are "Ended"
      statusLabel = intl.formatMessage({ defaultMessage: 'Ended', description: 'Episode status: ended' })
    }

    sessionItems.push({
      key: 'episode-status',
      label: <FormattedMessage defaultMessage='Status' description='Label for episode status' />,
      textValue: statusLabel,
      testID: 'ClientDetails-episode-status',
    })

    // Session type
    sessionItems.push({
      key: 'session-type',
      label: <FormattedMessage defaultMessage='Session type' description='Label for session type (video/in-person)' />,
      textValue: appointment
        ? formatMeetingType(appointment.meetingFormat, intl)
        : intl.formatMessage({ defaultMessage: 'N/A', description: 'Not applicable' }),
      testID: 'ClientDetails-session-type',
    })

    // Start date
    if (episode.startDate) {
      const startDate = new Date(episode.startDate)
      const formattedStartDate = intl.formatDate(startDate, { month: 'long', day: 'numeric', year: 'numeric' })

      sessionItems.push({
        key: 'start-date',
        label: <FormattedMessage defaultMessage='Start date' description='Label for therapy start date' />,
        textValue: formattedStartDate,
        testID: 'ClientDetails-start-date',
      })
    } else {
      sessionItems.push({
        key: 'start-date',
        label: <FormattedMessage defaultMessage='Start date' description='Label for therapy start date' />,
        textValue: intl.formatMessage({ defaultMessage: 'N/A', description: 'Not applicable' }),
        testID: 'ClientDetails-start-date',
      })
    }

    return { summaryText, items: sessionItems, careTypeCapitalized }
  }, [details, intl])

  if (items.length === 0 && !summaryText) {
    return null
  }

  return (
    <SectionWrapper>
      <StyledDivider height={1} color={colors.borderDefault} />
      <ContentWrapper>
        <Display
          size={DisplaySize.EYEBROW}
          text={
            <FormattedMessage
              defaultMessage='{careType} details'
              description='Header for care type details section (e.g., Therapy details, Coaching details)'
              values={{ careType: careTypeCapitalized }}
            />
          }
        />
        {summaryText && <BodyText size={BodyTextSize.SMALL} text={summaryText} />}
        {items.length > 0 && <ClientDetailsSideBarSection items={items} />}
      </ContentWrapper>
    </SectionWrapper>
  )
}
